import React from 'react';
import { ArrowRight, Cable, Check, Clock, ExternalLink, Info, ListTree, Minus, Ruler, Volume2 } from 'lucide-react';
import { amazonHref, amazonLinkState, countWord, depthSpread, footprintArea, guideAllowsLinks, identityState } from '../utils/editorialGuide.mjs';

// Renders a reviewed editorial guide from local fixture data (src/content/editorial/*.json).
// No database access, no stored prices/ratings/delivery text. An Amazon button appears only for a model whose
// listing has been matched and verified in the fixture and only in a published guide; otherwise the card says what is pending.
// Layout: prose stays in a ~70-character column; tables and cards use the wider container.

const th = 'px-4 py-3 text-left text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 whitespace-nowrap';
const td = 'px-4 py-3 align-top text-[15px] leading-6 text-slate-700 dark:text-slate-200';
const stickyTh = `${td} sticky left-0 z-10 bg-white font-semibold text-left shadow-[1px_0_0_0_rgb(226_232_240)] dark:bg-slate-900 dark:shadow-[1px_0_0_0_rgb(51_65_85)]`;
const card = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-900/60';
const link = 'text-blue-600 underline decoration-blue-600/40 underline-offset-4 transition hover:decoration-blue-600 focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-blue-400 dark:decoration-blue-400/40 dark:hover:decoration-blue-400 dark:focus-visible:ring-offset-slate-900';
const prose = 'max-w-[44rem] text-[1.0625rem] leading-[1.7] text-slate-700 dark:text-slate-200 md:text-[1.125rem]';
const ICONS = { speaker: Volume2, 'usb-c': Cable, stand: Ruler };
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function Section({ title, children, id, lead }) {
  return (
    <section id={id} className="mt-16 scroll-mt-20">
      <h2 className="text-[1.75rem] font-bold leading-tight tracking-tight text-slate-900 dark:text-white md:text-3xl">{title}</h2>
      {lead && <p className={`${prose} mt-3 text-slate-600 dark:text-slate-300`}>{lead}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function heightRange(f) {
  return f.heightMinIn === f.heightMaxIn ? `${f.heightMinIn} in` : `${f.heightMinIn}–${f.heightMaxIn} in`;
}

function ModelIcon({ model, className = 'h-5 w-5' }) {
  const Icon = ICONS[model.icon] || Ruler;
  return <Icon className={className} aria-hidden="true" />;
}

function PointList({ items, kind }) {
  const Icon = kind === 'plus' ? Check : Minus;
  const tone = kind === 'plus' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
  return (
    <ul className="space-y-2">
      {items.map((t) => (
        <li key={t} className="flex gap-2.5 text-[15px] leading-6">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tone}`}><Icon className="h-3.5 w-3.5" aria-hidden="true" /></span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Chips({ items }) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="At a glance">
      {items.map((g) => <li key={g} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[13px] text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">{g}</li>)}
    </ul>
  );
}

/** Contents card built from the sections that exist in this guide (plain anchors: works without JavaScript). */
function Contents({ guide }) {
  const items = [
    guide.verdict && ['short-answer', guide.verdict.heading],
    ['quick-picks', 'Quick picks'],
    ['footprint', 'Desk footprint and stand'],
    ['connections', 'Screen and connections'],
    ['models', `The ${countWord(guide.models.length)} models`],
    ...guide.guidance.map((g) => [slug(g.heading), g.heading]),
    ['methodology', 'How we researched this'],
    ['faq', 'FAQ'],
    ['sources', 'Sources'],
  ].filter(Boolean);
  return (
    <nav aria-label="In this guide" data-testid="toc" className={`${card} mt-8 p-5`}>
      <p className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"><ListTree className="h-4 w-4" aria-hidden="true" />In this guide</p>
      <ol className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1.5 text-[15px] sm:grid-cols-2 lg:grid-cols-3">
        {items.map(([id, label]) => (
          <li key={id}><a href={`#${id}`} className={`${link} no-underline hover:underline`}>{label}</a></li>
        ))}
      </ol>
    </nav>
  );
}

/** Dell's overall height with stand, as ranges on one shared scale (decorative; the table below carries the numbers). */
function HeightRanges({ models }) {
  const lo = Math.floor(Math.min(...models.map((m) => m.footprint.heightMinIn)) - 1);
  const hi = Math.ceil(Math.max(...models.map((m) => m.footprint.heightMaxIn)) + 1);
  const pct = (v) => ((v - lo) / (hi - lo)) * 100;
  return (
    <div className={`${card} mt-6 p-5`} data-testid="height-ranges">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Where the screen can sit</h3>
      <p className="mt-1 text-[15px] text-slate-600 dark:text-slate-300">Overall height with the stand, from Dell&apos;s dimensions ({lo}–{hi} in scale).</p>
      <ul className="mt-4 space-y-3">
        {models.map((m) => {
          const f = m.footprint; const fixed = f.heightMinIn === f.heightMaxIn;
          return (
            <li key={m.id} className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[13rem_1fr_7rem] sm:gap-4">
              <span className="text-[15px] font-medium">{m.modelCode}</span>
              <span className="relative block h-3 rounded-full bg-slate-100 dark:bg-slate-800" aria-hidden="true">
                <span className="absolute top-0 h-3 rounded-full bg-blue-500" style={{ left: `${pct(f.heightMinIn)}%`, width: `${Math.max(pct(f.heightMaxIn) - pct(f.heightMinIn), 1.8)}%` }} />
              </span>
              <span className="text-[15px] tabular-nums text-slate-600 dark:text-slate-300">{heightRange(f)}{fixed ? ' (fixed)' : ''}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AmazonAction({ model, partnerTag, guide }) {
  const href = amazonHref(model, partnerTag, guide);
  if (href) {
    return (
      <div className="flex flex-col gap-1">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow sponsored"
        data-testid={`amazon-link-${model.id}`}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
      >
        View on Amazon <span className="ml-2 text-xs font-normal">(affiliate link)</span>
      </a>
      <span data-testid={`affiliate-disclosure-${model.id}`} className="text-xs text-slate-500 dark:text-slate-400">{guide.disclosure}</span>
      </div>
    );
  }
  const state = amazonLinkState(model);
  let message = 'Amazon listing pending verification: no link is shown until the exact model is matched.';
  if (identityState(model).verified) message = 'Product identified on Amazon. Offer details (seller, condition and availability) are still being verified, so no buy link is shown yet.';
  if (state.show) message = guideAllowsLinks(guide) ? 'Amazon link unavailable in this build.' : 'Amazon offer verified. The buy link stays hidden while this guide is a draft.';
  else if (state.identityVerified && state.offerVerified) message = 'Amazon offer verified; it is waiting for an editor to accept the seller or listing, so no buy link is shown.';
  return (
    <p data-testid={`amazon-pending-${model.id}`} className="inline-flex items-start gap-2 text-sm text-slate-500 dark:text-slate-400"><Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </p>
  );
}

export function EditorialShortlist({ guide, partnerTag = import.meta.env?.VITE_AMAZON_PARTNER_TAG }) {
  if (!guide) return null;
  const byId = Object.fromEntries(guide.models.map((m) => [m.id, m]));

  return (
    <article className="mx-auto max-w-5xl px-4 pb-16 pt-8 text-slate-800 dark:text-slate-100" data-editorial-guide={guide.slug}>
      <p data-testid="disclosure" className="flex max-w-[44rem] items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[15px] leading-6 text-slate-700 dark:border-slate-700/60 dark:bg-slate-800/60 dark:text-slate-200">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        <span>{guide.disclosure}</span>
      </p>

      {/* guide.summary is shown in the page header band, so it is not repeated here */}
      <div className="mt-8 space-y-5">
        {guide.intro.map((p, i) => <p key={i} className={`${prose} ${i === 0 ? 'text-[1.1875rem] md:text-[1.25rem] text-slate-800 dark:text-slate-100' : ''}`}>{p}</p>)}
      </div>

      <Contents guide={guide} />

      {guide.verdict && (
        <section id="short-answer" data-testid="short-answer" className="mt-14 scroll-mt-20 rounded-2xl border border-blue-200 bg-blue-50/70 p-6 dark:border-blue-500/30 dark:bg-blue-500/10 md:p-8">
          <h2 className="text-[1.75rem] font-bold leading-tight tracking-tight text-slate-900 dark:text-white md:text-3xl">{guide.verdict.heading}</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-10">
            {guide.verdict.paragraphs.map((p, i) => <p key={i} className="text-[1.0625rem] leading-[1.7] text-slate-700 dark:text-slate-200 md:text-[1.125rem]">{p}</p>)}
          </div>
        </section>
      )}

      <Section title="Quick picks" id="quick-picks">
        <ul className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {guide.quickPicks.map((pick) => {
            const m = byId[pick.modelId];
            return (
              <li key={pick.modelId} className={`${card} flex flex-col p-5 transition hover:border-blue-400/60 hover:shadow-md dark:hover:border-blue-400/40`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400"><ModelIcon model={m} /></span>
                  <p className="text-[13px] font-semibold uppercase leading-snug tracking-wide text-blue-600 dark:text-blue-400">{pick.role}</p>
                </div>
                <p className="mt-4 text-lg font-bold leading-snug text-slate-900 dark:text-white md:min-h-[3.25rem]">{m.name}</p>
                {m.glance && <div className="mt-3"><Chips items={m.glance} /></div>}
                {m.pick && (
                  <div className="mt-4 space-y-2.5 border-t border-slate-200 pt-4 dark:border-slate-700/60">
                    <PointList kind="plus" items={[m.pick.plus]} />
                    <PointList kind="minus" items={[m.pick.minus]} />
                  </div>
                )}
                <a href={`#${pick.modelId}`} className={`${link} mt-auto inline-flex items-center gap-1.5 pt-5 text-[15px] font-medium no-underline hover:underline`}>
                  Read the full review <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="Desk footprint and stand" id="footprint">
        <HeightRanges models={guide.models} />
        <div className={`${card} mt-6 overflow-hidden`}>
          <div className="overflow-x-auto" data-testid="footprint-table" role="region" aria-label="Desk footprint and stand comparison, scrollable" tabIndex={0}>
            <table className="w-full min-w-[640px] divide-y divide-slate-200 dark:divide-slate-700/60">
              <caption className="sr-only">Stand footprint, height and adjustment for each Dell monitor, from Dell&apos;s published dimensions</caption>
              <thead className="bg-slate-50 dark:bg-slate-800/60">
                <tr>
                  <th scope="col" className={th}>Model</th>
                  <th scope="col" className={th}>Width × depth</th>
                  <th scope="col" className={th}>Calculated footprint</th>
                  <th scope="col" className={th}>Height</th>
                  <th scope="col" className={th}>Stand adjustment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700/60 dark:bg-slate-900">
                {guide.models.map((m) => (
                  <tr key={m.id}>
                    <th scope="row" className={stickyTh}>{m.name}</th>
                    <td className={td}>{m.footprint.widthIn} × {m.footprint.depthIn} in</td>
                    <td className={td}>≈{footprintArea(m)} sq in</td>
                    <td className={td}>{heightRange(m.footprint)}</td>
                    <td className={td}>
                      Height: {m.stand.heightAdjust}<br />Tilt {m.stand.tilt}<br />Swivel {m.stand.swivel}<br />Pivot {m.stand.pivot}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-3 max-w-[44rem] text-[15px] leading-6 text-slate-600 dark:text-slate-300">
          Width, depth and height are Dell&apos;s published dimensions with the stand; the footprint is width × depth, calculated by us.
          Stand depth differs by only {depthSpread(guide.models)} in across these {countWord(guide.models.length)}.
        </p>
      </Section>

      <Section title="Screen and connections" id="connections">
        <div className={`${card} overflow-hidden`}>
          <div className="overflow-x-auto" data-testid="specs-table" role="region" aria-label="Screen and connections comparison, scrollable" tabIndex={0}>
            <table className="w-full min-w-[980px] divide-y divide-slate-200 dark:divide-slate-700/60">
              <caption className="sr-only">Screen, video inputs, USB, USB-C, speakers, VESA and warranty for each Dell monitor, from Dell&apos;s US product pages</caption>
              <thead className="bg-slate-50 dark:bg-slate-800/60">
                <tr>
                  <th scope="col" className={th}>Model</th>
                  <th scope="col" className={th}>Screen</th>
                  <th scope="col" className={th}>Video inputs</th>
                  <th scope="col" className={th}>USB</th>
                  <th scope="col" className={th}>USB-C</th>
                  <th scope="col" className={th}>Speakers</th>
                  <th scope="col" className={th}>VESA</th>
                  <th scope="col" className={th}>Warranty (Dell US)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700/60 dark:bg-slate-900">
                {guide.models.map((m) => (
                  <tr key={m.id}>
                    <th scope="row" className={stickyTh}>{m.name}</th>
                    <td className={`${td} min-w-[11rem]`}>{m.display}</td>
                    <td className={`${td} min-w-[10rem]`}>{m.videoInputs}</td>
                    <td className={`${td} min-w-[14rem]`}>{m.usb}</td>
                    <td className={`${td} min-w-[12rem]`}>{m.usbC}</td>
                    <td className={`${td} min-w-[8rem]`}>{m.speakers}</td>
                    <td className={`${td} whitespace-nowrap`}>{m.vesa}</td>
                    <td className={`${td} min-w-[9rem]`}>{m.warrantyUs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section title={`The ${countWord(guide.models.length)} models`} id="models">
        <div className="space-y-8">
          {guide.models.map((m) => (
            <div key={m.id} id={m.id} data-model={m.id} className={`${card} scroll-mt-20 p-6 md:p-8`}>
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400"><ModelIcon model={m} className="h-6 w-6" /></span>
                <div>
                  <p className="text-[13px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">{m.role}</p>
                  <h3 className="mt-1 text-xl font-bold leading-snug text-slate-900 dark:text-white md:text-2xl">{m.name}</h3>
                </div>
              </div>
              {m.glance && <div className="mt-4"><Chips items={m.glance} /></div>}
              <p className="mt-4 text-[15px] leading-6 text-slate-600 dark:text-slate-300">{m.display}. {m.panelNotes}. {m.weight}.</p>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h4 className="mb-3 text-[15px] font-semibold text-slate-900 dark:text-white">Strengths</h4>
                  <PointList kind="plus" items={m.strengths} />
                </div>
                <div>
                  <h4 className="mb-3 text-[15px] font-semibold text-slate-900 dark:text-white">Compromises</h4>
                  <PointList kind="minus" items={m.compromises} />
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                <p className="rounded-xl bg-slate-50 p-4 text-[15px] leading-6 dark:bg-slate-800/60"><strong className="block text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Best for</strong>{m.bestFor}</p>
                <p className="rounded-xl bg-slate-50 p-4 text-[15px] leading-6 dark:bg-slate-800/60"><strong className="block text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Not for</strong>{m.notFor}</p>
              </div>
              {m.listingNote && (
                <p data-testid={`listing-note-${m.id}`} className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-[15px] text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">{m.listingNote}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200 pt-5 dark:border-slate-700/60">
                <AmazonAction model={m} partnerTag={partnerTag} guide={guide} />
                <a href={m.dell.url} target="_blank" rel="noopener noreferrer" className={`${link} inline-flex items-center gap-1.5 text-[15px]`}>
                  Dell product page (source) <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {guide.guidance.map((g) => (
        <Section key={g.heading} title={g.heading} id={slug(g.heading)}>
          <div className="space-y-4">{g.paragraphs.map((p, i) => <p key={i} className={prose}>{p}</p>)}</div>
        </Section>
      ))}

      <Section title="How we researched this" id="methodology">
        <ul className="max-w-[44rem] list-disc space-y-2 pl-5 text-[1rem] leading-7 text-slate-700 marker:text-slate-400 dark:text-slate-200">
          {guide.research.method.map((m) => <li key={m}>{m}</li>)}
        </ul>
        <p className="mt-4 max-w-[44rem] text-[15px] text-slate-600 dark:text-slate-300">
          Research date: {guide.research.date}. Editorial reviewer: {guide.research.reviewer || 'pending'}.
        </p>
      </Section>

      <Section title="FAQ" id="faq">
        <dl className="max-w-[44rem] space-y-3">
          {guide.faq.map((f) => (
            <div key={f.q} className={`${card} p-5`}>
              <dt className="text-[1.0625rem] font-semibold text-slate-900 dark:text-white">{f.q}</dt>
              <dd className="mt-2 text-[1rem] leading-7 text-slate-700 dark:text-slate-200">{f.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title="Sources" id="sources">
        <ul className="max-w-[44rem] list-disc space-y-2 pl-5 text-[15px] leading-6 marker:text-slate-400">
          {guide.sources.map((s) => (
            <li key={s.url}>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className={link}>{s.label}</a>
              {' '}<span className="text-slate-500 dark:text-slate-400">— read {s.retrieved}</span>
            </li>
          ))}
        </ul>
      </Section>

      <p className="mt-14 max-w-[44rem] text-[15px] text-slate-600 dark:text-slate-300">
        Looking for something else? <a href={`/category/${guide.breadcrumb.categorySlug}`} className={link}>Browse all {guide.breadcrumb.categoryName.toLowerCase()}</a>.
      </p>
    </article>
  );
}
